import Company from '../interfaces/Company';
import { Subscription } from '../interfaces/Subscription';
import { UsageResponse } from '../services/billingService';
import {
  CREDITS_PER_CALL_MINUTE,
  CREDITS_PER_CHAT_MESSAGE,
  resolvedPoolRemaining,
  showCreditPoolSection,
} from './creditUsageDisplay';
import { isEnterpriseCustomer } from './pricingUpgradeRules';

export const LOW_BALANCE_THRESHOLD_MINUTES = 100;
export const LOW_BALANCE_THRESHOLD_CREDITS =
  LOW_BALANCE_THRESHOLD_MINUTES * CREDITS_PER_CALL_MINUTE;
export const LOW_BALANCE_THRESHOLD_MESSAGES =
  LOW_BALANCE_THRESHOLD_CREDITS / CREDITS_PER_CHAT_MESSAGE;

/**
 * Low-balance upgrade prompt: remaining allowance below the equivalent of
 * 100 call minutes (spec: specs/low-balance-upgrade-banner.md). Enterprise
 * has no self-serve upgrade path, so it never triggers.
 */
export function shouldShowLowBalanceBanner(
  usage: UsageResponse | null,
  company: Company | null
): boolean {
  if (!usage || !company) return false;
  if (isEnterpriseCustomer(company) || usage.enterprise === true) return false;
  if (usage.usage_billing_mode === 'unlimited') return false;

  if (showCreditPoolSection(usage)) {
    const remaining = resolvedPoolRemaining(usage);
    return remaining !== null && remaining < LOW_BALANCE_THRESHOLD_CREDITS;
  }

  if (usage.minutes_limit != null && usage.minutes_limit > 0) {
    if (
      usage.minutes_limit - usage.minutes_used <
      LOW_BALANCE_THRESHOLD_MINUTES
    ) {
      return true;
    }
  }
  if (usage.messages_limit != null && usage.messages_limit > 0) {
    if (
      usage.messages_limit - usage.messages_used <
      LOW_BALANCE_THRESHOLD_MESSAGES
    ) {
      return true;
    }
  }
  if (
    usage.minutes_limit == null &&
    typeof usage.minutes_remaining === 'number' &&
    usage.minutes_remaining < LOW_BALANCE_THRESHOLD_MINUTES
  ) {
    return true;
  }

  return false;
}

/**
 * Dismissals last one billing period: keyed to the Stripe period start when a
 * subscription exists, otherwise the current calendar month (free tier).
 */
export function lowBalanceDismissalKey(
  companyId: string,
  subscription: Subscription | null
): string {
  const period =
    subscription?.current_period_start != null
      ? String(subscription.current_period_start)
      : new Date().toISOString().slice(0, 7);
  return `low_balance_banner_dismissed:${companyId}:${period}`;
}

export function isLowBalanceBannerDismissed(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

export function dismissLowBalanceBanner(key: string): void {
  try {
    window.localStorage.setItem(key, '1');
  } catch {
    // Storage unavailable (private mode): banner stays visible this session.
  }
}
